// src/services/loadLetterService.ts

import { supabase } from '../supabase/config';
import { emailService } from './emailService';
import {
  LoadLetter,
  LoadLetterTemplate,
  LoadLetterSettings,
  LoadLetterQuery,
  LoadLetterRelease,
  LoadLetterFilters,
  LoadLetterSortOptions,
  Semester,
  LoadLetterStatus,
  AcknowledgmentSummary,
  UtilizationDistribution,
} from '../types/loadLetter';

// ============================================================================
// LOAD LETTER CRUD OPERATIONS
// ============================================================================

/**
 * Fetch all load letters with optional filters
 */
export const getLoadLetters = async (
  filters?: LoadLetterFilters,
  sortOptions?: LoadLetterSortOptions,
  includeSuperseded: boolean = false
): Promise<LoadLetter[]> => {
  let query = supabase
    .from('load_letters')
    .select('*');

  // Hide superseded versions by default so callers see only the current letter per staff/semester
  if (!includeSuperseded) {
    query = query.is('superseded_at', null);
  }

  // Apply filters
  if (filters) {
    if (filters.academicYear) {
      query = query.eq('academic_year', filters.academicYear);
    }
    if (filters.semester) {
      query = query.eq('semester', filters.semester);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.department) {
      query = query.eq('department', filters.department);
    }
    if (filters.curriculumArea) {
      query = query.eq('curriculum_area', filters.curriculumArea);
    }
    if (filters.staffId) {
      query = query.eq('staff_id', filters.staffId);
    }
    if (filters.staffCode) {
      query = query.eq('staff_code', filters.staffCode);
    }
    if (filters.searchQuery) {
      query = query.or(`staff_name.ilike.%${filters.searchQuery}%,staff_code.ilike.%${filters.searchQuery}%`);
    }
  }

  // Apply sorting
  if (sortOptions) {
    query = query.order(sortOptions.field, { ascending: sortOptions.direction === 'asc' });
  } else {
    query = query.order('staff_name', { ascending: true });
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching load letters:', error);
    throw error;
  }

  // Transform snake_case to camelCase
  return (data || []).map(transformLoadLetterFromDB);
};

/**
 * Transform database row (snake_case) to LoadLetter (camelCase)
 */
const transformLoadLetterFromDB = (row: any): LoadLetter => {
  return {
    id: row.id,
    staffId: row.staff_id,
    staffName: row.staff_name,
    staffCode: row.staff_code,
    department: row.department,
    curriculumArea: row.curriculum_area,
    academicYear: row.academic_year,
    semester: row.semester,
    issueDate: row.issue_date,
    dueDate: row.due_date,
    status: row.status,
    generatedDate: row.generated_date,
    releasedDate: row.released_date,
    acknowledgedDate: row.acknowledged_date,
    fte: row.fte,
    baseMinutesPerFortnight: row.base_minutes_per_fortnight,
    targetMinutes: row.target_minutes,
    allocatedMinutes: row.allocated_minutes,
    teachingMinutes: row.teaching_minutes,
    allowanceMinutes: row.allowance_minutes,
    timetabledSubs: row.timetabled_subs,
    utilization: row.utilization,
    semester1Classes: row.semester1_classes || [],
    semester2Classes: row.semester2_classes || [],
    allowances: row.allowances || [],
    templateId: row.template_id,
    generatedHtml: row.generated_html,
    notes: row.notes,
    queries: [], // Queries are fetched separately
    version: row.version,
    supersededAt: row.superseded_at,
    supersededById: row.superseded_by_id,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    createdBy: row.created_by,
    lastModifiedBy: row.last_modified_by,
  };
};

/**
 * Get a single load letter by ID
 */
export const getLoadLetterById = async (id: string): Promise<LoadLetter | null> => {
  const { data, error } = await supabase
    .from('load_letters')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching load letter:', error);
    throw error;
  }

  return data ? transformLoadLetterFromDB(data) : null;
};

/**
 * Get load letters for a specific staff member
 * Only returns released, acknowledged, or queried letters (not draft or ready)
 */
export const getLoadLettersByStaffId = async (
  staffId: string,
  includeSuperseded: boolean = false
): Promise<LoadLetter[]> => {
  let query = supabase
    .from('load_letters')
    .select('*')
    .eq('staff_id', staffId)
    .in('status', ['released', 'acknowledged', 'queried'])
    .order('academic_year', { ascending: false })
    .order('semester', { ascending: false });

  if (!includeSuperseded) {
    query = query.is('superseded_at', null);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching staff load letters:', error);
    throw error;
  }

  return (data || []).map(transformLoadLetterFromDB);
};

/**
 * Create a new load letter
 */
/**
 * Build a stable content signature for a load letter.
 * Used to decide whether a regeneration produced different content.
 * Only fields visible to staff are compared — presentation/metadata is ignored.
 */
const hashLoadLetterContent = (letter: {
  fte: number;
  baseMinutesPerFortnight: number;
  targetMinutes: number;
  allocatedMinutes: number;
  teachingMinutes: number;
  allowanceMinutes: number;
  timetabledSubs: number;
  utilization: number;
  semester1Classes: any[];
  semester2Classes: any[];
  ruahTerm1Classes?: any[];
  ruahTerm2Classes?: any[];
  ruahTerm3Classes?: any[];
  ruahTerm4Classes?: any[];
  allowances: any[];
}): string => {
  const sortClasses = (arr: any[] = []) =>
    [...arr].sort((a, b) => String(a.id ?? '').localeCompare(String(b.id ?? '')));
  const sortAllowances = (arr: any[] = []) =>
    [...arr].sort((a, b) => String(a.id ?? '').localeCompare(String(b.id ?? '')));

  return JSON.stringify({
    fte: letter.fte,
    baseMinutesPerFortnight: letter.baseMinutesPerFortnight,
    targetMinutes: letter.targetMinutes,
    allocatedMinutes: letter.allocatedMinutes,
    teachingMinutes: letter.teachingMinutes,
    allowanceMinutes: letter.allowanceMinutes,
    timetabledSubs: letter.timetabledSubs,
    utilization: letter.utilization,
    semester1Classes: sortClasses(letter.semester1Classes),
    semester2Classes: sortClasses(letter.semester2Classes),
    ruahTerm1Classes: sortClasses(letter.ruahTerm1Classes),
    ruahTerm2Classes: sortClasses(letter.ruahTerm2Classes),
    ruahTerm3Classes: sortClasses(letter.ruahTerm3Classes),
    ruahTerm4Classes: sortClasses(letter.ruahTerm4Classes),
    allowances: sortAllowances(letter.allowances),
  });
};

export type UpsertAction = 'created' | 'updated' | 'unchanged';

/**
 * Upsert a load letter with content-aware versioning.
 *
 * - No existing letter for (staff, year, semester) → insert as version 1.
 * - Existing current letter, content identical → no DB write, returns 'unchanged'.
 * - Existing current letter, content differs → mark old as superseded, insert new version
 *   (status 'ready', acknowledged/released metadata cleared — staff must re-acknowledge).
 *
 * This is safe to run on every "Generate All Letters" click: only staff whose load
 * actually changed will see a new version created.
 */
export const upsertLoadLetterIfChanged = async (
  loadLetter: Omit<LoadLetter, 'id' | 'createdAt' | 'updatedAt'>
): Promise<{ action: UpsertAction; letter: LoadLetter }> => {
  // Find the current (non-superseded) letter for this staff/year/semester
  const { data: existingRows, error: lookupError } = await supabase
    .from('load_letters')
    .select('*')
    .eq('staff_id', loadLetter.staffId)
    .eq('academic_year', loadLetter.academicYear)
    .eq('semester', loadLetter.semester)
    .is('superseded_at', null);

  if (lookupError) {
    console.error('Error looking up existing load letter:', lookupError);
    throw lookupError;
  }

  const existing = existingRows && existingRows.length > 0 ? existingRows[0] : null;

  if (!existing) {
    const inserted = await createLoadLetter(loadLetter);
    return { action: 'created', letter: inserted };
  }

  const existingLetter = transformLoadLetterFromDB(existing);
  const existingHash = hashLoadLetterContent(existingLetter);
  const newHash = hashLoadLetterContent(loadLetter);

  if (existingHash === newHash) {
    return { action: 'unchanged', letter: existingLetter };
  }

  const newVersion = (existing.version ?? 1) + 1;
  const now = new Date().toISOString();

  const { data: inserted, error: insertError } = await supabase
    .from('load_letters')
    .insert([{
      staff_id: loadLetter.staffId,
      staff_name: loadLetter.staffName,
      staff_code: loadLetter.staffCode,
      department: loadLetter.department,
      curriculum_area: loadLetter.curriculumArea,
      academic_year: loadLetter.academicYear,
      semester: loadLetter.semester,
      issue_date: loadLetter.issueDate,
      due_date: loadLetter.dueDate,
      status: 'ready',
      generated_date: loadLetter.generatedDate,
      released_date: null,
      acknowledged_date: null,
      fte: loadLetter.fte,
      base_minutes_per_fortnight: loadLetter.baseMinutesPerFortnight,
      target_minutes: loadLetter.targetMinutes,
      allocated_minutes: loadLetter.allocatedMinutes,
      teaching_minutes: loadLetter.teachingMinutes,
      allowance_minutes: loadLetter.allowanceMinutes,
      timetabled_subs: loadLetter.timetabledSubs,
      utilization: loadLetter.utilization,
      semester1_classes: loadLetter.semester1Classes,
      semester2_classes: loadLetter.semester2Classes,
      allowances: loadLetter.allowances,
      template_id: loadLetter.templateId,
      generated_html: loadLetter.generatedHtml,
      notes: loadLetter.notes,
      created_by: loadLetter.createdBy,
      version: newVersion,
    }])
    .select()
    .single();

  if (insertError) {
    console.error('Error inserting new load letter version:', insertError);
    throw insertError;
  }

  const { error: supersedeError } = await supabase
    .from('load_letters')
    .update({ superseded_at: now, superseded_by_id: inserted.id })
    .eq('id', existing.id);

  if (supersedeError) {
    console.error('Error marking previous letter as superseded:', supersedeError);
    throw supersedeError;
  }

  return { action: 'updated', letter: transformLoadLetterFromDB(inserted) };
};

/**
 * Return every version of a staff member's load letter for a given semester
 * that was actually released to them, newest first. Used by the teacher
 * history view, which presents each one as "exactly what you received".
 *
 * Versions that were generated but never released (e.g. regenerated before
 * release, or the double-counted 2027 drafts from #280) are excluded: the
 * staff member never received them. Superseding a letter leaves its
 * released_date untouched, so released-then-replaced versions still show.
 */
export const getLoadLetterHistory = async (
  staffId: string,
  academicYear: string,
  semester: Semester
): Promise<LoadLetter[]> => {
  const { data, error } = await supabase
    .from('load_letters')
    .select('*')
    .eq('staff_id', staffId)
    .eq('academic_year', academicYear)
    .eq('semester', semester)
    .not('released_date', 'is', null)
    .order('version', { ascending: false });

  if (error) {
    console.error('Error fetching load letter history:', error);
    throw error;
  }

  return (data || []).map(transformLoadLetterFromDB);
};

export const createLoadLetter = async (
  loadLetter: Omit<LoadLetter, 'id' | 'createdAt' | 'updatedAt'>
): Promise<LoadLetter> => {
  const { data, error } = await supabase
    .from('load_letters')
    .insert([{
      staff_id: loadLetter.staffId,
      staff_name: loadLetter.staffName,
      staff_code: loadLetter.staffCode,
      department: loadLetter.department,
      curriculum_area: loadLetter.curriculumArea,
      academic_year: loadLetter.academicYear,
      semester: loadLetter.semester,
      issue_date: loadLetter.issueDate,
      due_date: loadLetter.dueDate,
      status: loadLetter.status,
      generated_date: loadLetter.generatedDate,
      released_date: loadLetter.releasedDate,
      acknowledged_date: loadLetter.acknowledgedDate,
      fte: loadLetter.fte,
      base_minutes_per_fortnight: loadLetter.baseMinutesPerFortnight,
      target_minutes: loadLetter.targetMinutes,
      allocated_minutes: loadLetter.allocatedMinutes,
      teaching_minutes: loadLetter.teachingMinutes,
      allowance_minutes: loadLetter.allowanceMinutes,
      timetabled_subs: loadLetter.timetabledSubs,
      utilization: loadLetter.utilization,
      semester1_classes: loadLetter.semester1Classes,
      semester2_classes: loadLetter.semester2Classes,
      allowances: loadLetter.allowances,
      template_id: loadLetter.templateId,
      generated_html: loadLetter.generatedHtml,
      notes: loadLetter.notes,
      created_by: loadLetter.createdBy,
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating load letter:', error);
    throw error;
  }

  return transformLoadLetterFromDB(data);
};

/**
 * Update an existing load letter
 */
export const updateLoadLetter = async (
  id: string,
  updates: Partial<LoadLetter>
): Promise<LoadLetter> => {
  const updateData: any = {};

  if (updates.status) updateData.status = updates.status;
  if (updates.releasedDate) updateData.released_date = updates.releasedDate;
  if (updates.acknowledgedDate) updateData.acknowledged_date = updates.acknowledgedDate;
  if (updates.notes !== undefined) updateData.notes = updates.notes;
  if (updates.generatedHtml) updateData.generated_html = updates.generatedHtml;
  if (updates.lastModifiedBy) updateData.last_modified_by = updates.lastModifiedBy;

  const { data, error } = await supabase
    .from('load_letters')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating load letter:', error);
    throw error;
  }

  return transformLoadLetterFromDB(data);
};

/**
 * Delete a load letter
 */
export const deleteLoadLetter = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('load_letters')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting load letter:', error);
    throw error;
  }
};

/**
 * Delete multiple load letters (bulk operation)
 */
export const deleteLoadLetters = async (letterIds: string[]): Promise<number> => {
  const { error, count } = await supabase
    .from('load_letters')
    .delete()
    .in('id', letterIds);

  if (error) {
    console.error('Error deleting load letters:', error);
    throw error;
  }

  return count || letterIds.length;
};

/**
 * Acknowledge a load letter (staff action)
 */
export const acknowledgeLoadLetter = async (id: string): Promise<LoadLetter> => {
  const { data, error } = await supabase
    .from('load_letters')
    .update({
      status: 'acknowledged',
      acknowledged_date: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error acknowledging load letter:', error);
    throw error;
  }

  return transformLoadLetterFromDB(data);
};

/**
 * Mark a single letter as ready for release (admin review complete)
 */
export const markLetterAsReady = async (letterId: string): Promise<LoadLetter> => {
  const { data, error } = await supabase
    .from('load_letters')
    .update({
      status: 'ready',
    })
    .eq('id', letterId)
    .select()
    .single();

  if (error) {
    console.error('Error marking letter as ready:', error);
    throw error;
  }

  return transformLoadLetterFromDB(data);
};

/**
 * Mark multiple letters as ready for release (bulk operation)
 */
export const markLettersAsReady = async (letterIds: string[]): Promise<number> => {
  const { error, count } = await supabase
    .from('load_letters')
    .update({
      status: 'ready',
    })
    .in('id', letterIds);

  if (error) {
    console.error('Error marking letters as ready:', error);
    throw error;
  }

  return count || letterIds.length;
};

// v1.6.0: map Semester enum values to human-readable labels for email subjects.
function semesterLabel(semester: Semester, academicYear: string): string {
  switch (semester) {
    case 'semester1': return `Semester 1, ${academicYear}`;
    case 'semester2': return `Semester 2, ${academicYear}`;
    case 'both':      return academicYear;
    default:          return String(semester) + (academicYear ? `, ${academicYear}` : '');
  }
}

// v1.6.0: look up teacher emails by staff_code for a list of load letters.
// Returns a Map keyed by letter_id → { email, firstName }.
async function resolveTeacherContactsForLetters(
  letters: { id: string; staffCode: string; staffName: string }[]
): Promise<Map<string, { email: string; firstName: string }>> {
  const contactMap = new Map<string, { email: string; firstName: string }>();
  if (letters.length === 0) return contactMap;

  const staffCodes = [...new Set(letters.map(l => l.staffCode).filter(Boolean))];
  if (staffCodes.length === 0) return contactMap;

  const { data: staffRows, error } = await supabase
    .from('staff')
    .select('email, initial, staff_code, name, first_name')
    .or(staffCodes.map(c => `initial.eq.${c},staff_code.eq.${c}`).join(','));

  if (error) {
    console.error('resolveTeacherContactsForLetters: staff lookup failed', error);
    return contactMap;
  }

  // Build lookup by both `initial` and `staff_code` since different rows use different fields
  const staffByCode = new Map<string, any>();
  for (const row of staffRows || []) {
    if (row.initial) staffByCode.set(row.initial, row);
    if (row.staff_code) staffByCode.set(row.staff_code, row);
  }

  for (const letter of letters) {
    const staff = staffByCode.get(letter.staffCode);
    if (staff?.email) {
      const firstName =
        staff.first_name ||
        (staff.name ? String(staff.name).split(' ')[0] : '') ||
        (letter.staffName ? letter.staffName.split(' ')[0] : 'there');
      contactMap.set(letter.id, { email: staff.email, firstName });
    }
  }
  return contactMap;
}

/**
 * Release a single load letter to staff
 */
export const releaseLoadLetter = async (letterId: string): Promise<LoadLetter> => {
  const releaseDate = new Date().toISOString();

  const { data, error } = await supabase
    .from('load_letters')
    .update({
      status: 'released',
      released_date: releaseDate,
    })
    .eq('id', letterId)
    .select()
    .single();

  if (error) {
    console.error('Error releasing load letter:', error);
    throw error;
  }

  const letter = transformLoadLetterFromDB(data);

  // v1.6.0: notify the teacher. Fire-and-forget; failures must never block release.
  try {
    const contacts = await resolveTeacherContactsForLetters([
      { id: letter.id, staffCode: letter.staffCode, staffName: letter.staffName }
    ]);
    const contact = contacts.get(letter.id);
    if (contact) {
      emailService
        .sendLoadLetterReleased([{
          letterId: letter.id,
          teacherEmail: contact.email,
          teacherFirstName: contact.firstName,
          semesterLabel: semesterLabel(letter.semester, letter.academicYear)
        }])
        .catch(err => console.error('Load letter release email dispatch failed', err));
    }
  } catch (notifyErr) {
    console.error('Load letter release notification setup failed', notifyErr);
  }

  return letter;
};

/**
 * Release load letters (bulk operation)
 */
export const releaseLoadLetters = async (
  letterIds: string[],
  releaseInfo: {
    semester: Semester;
    academicYear: string;
    departmentFilter?: string;
    curriculumAreaFilter?: string;
    createdBy: string;
    notes?: string;
  }
): Promise<LoadLetterRelease> => {
  // Update all letters to 'released' status
  const releaseDate = new Date().toISOString();

  const { error: updateError } = await supabase
    .from('load_letters')
    .update({
      status: 'released',
      released_date: releaseDate,
    })
    .in('id', letterIds);

  if (updateError) {
    console.error('Error releasing load letters:', updateError);
    throw updateError;
  }

  // v1.6.0: notify each teacher. Fire-and-forget — bulk release must succeed
  // regardless of email delivery outcomes.
  try {
    const { data: releasedRows } = await supabase
      .from('load_letters')
      .select('id, staff_code, staff_name')
      .in('id', letterIds);

    if (releasedRows && releasedRows.length > 0) {
      const letterShape = releasedRows.map(r => ({
        id: r.id,
        staffCode: r.staff_code,
        staffName: r.staff_name
      }));
      const contacts = await resolveTeacherContactsForLetters(letterShape);
      const emailPayload = letterShape
        .map(l => {
          const c = contacts.get(l.id);
          if (!c) return null;
          return {
            letterId: l.id,
            teacherEmail: c.email,
            teacherFirstName: c.firstName,
            semesterLabel: semesterLabel(releaseInfo.semester, releaseInfo.academicYear)
          };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null);

      if (emailPayload.length > 0) {
        emailService
          .sendLoadLetterReleased(emailPayload)
          .catch(err => console.error('Bulk load letter release emails failed', err));
      }
    }
  } catch (notifyErr) {
    console.error('Bulk release notification setup failed', notifyErr);
  }

  // Create release record
  const { data, error } = await supabase
    .from('load_letter_releases')
    .insert([{
      release_date: releaseDate,
      semester: releaseInfo.semester,
      academic_year: releaseInfo.academicYear,
      department_filter: releaseInfo.departmentFilter,
      curriculum_area_filter: releaseInfo.curriculumAreaFilter,
      staff_ids: letterIds,
      total_letters: letterIds.length,
      successful_releases: letterIds.length,
      failed_releases: 0,
      emails_sent: 0, // TODO: Implement email sending
      emails_failed: 0,
      created_by: releaseInfo.createdBy,
      notes: releaseInfo.notes,
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating release record:', error);
    throw error;
  }

  return data;
};

// ============================================================================
// TEMPLATE OPERATIONS
// ============================================================================

/**
 * Transform database row (snake_case) to LoadLetterTemplate (camelCase)
 */
const transformTemplateFromDB = (row: any): LoadLetterTemplate => {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    headerHtml: row.header_html,
    letterOpeningHtml: row.letter_opening_html,
    letterBodyHtml: row.letter_body_html,
    meetingScheduleHtml: row.meeting_schedule_html,
    letterClosingHtml: row.letter_closing_html,
    signatureHtml: row.signature_html,
    introductionHtml: row.introduction_html,
    modelExplanationHtml: row.model_explanation_html,
    yardDutyHtml: row.yard_duty_html,
    footerHtml: row.footer_html,
    notesSectionHtml: row.notes_section_html,
    logoUrl: row.logo_url,
    showLogo: row.show_logo,
    showOratoryLeader: row.show_oratory_leader,
    showPolPosition: row.show_pol_position,
    isDefault: row.is_default,
    isActive: row.is_active,
    semester: row.semester,
    lastUsed: row.last_used,
    usageCount: row.usage_count,
    version: row.version,
    parentTemplateId: row.parent_template_id,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    createdBy: row.created_by,
    lastModifiedBy: row.last_modified_by,
  };
};

/**
 * Get all active templates
 */
export const getActiveTemplates = async (): Promise<LoadLetterTemplate[]> => {
  const { data, error } = await supabase
    .from('load_letter_templates')
    .select('*')
    .eq('is_active', true)
    .order('name', { ascending: true});

  if (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }

  return (data || []).map(transformTemplateFromDB);
};

/**
 * Get default template
 */
export const getDefaultTemplate = async (): Promise<LoadLetterTemplate | null> => {
  const { data, error } = await supabase
    .from('load_letter_templates')
    .select('*')
    .eq('is_default', true)
    .eq('is_active', true)
    .single();

  if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
    console.error('Error fetching default template:', error);
    throw error;
  }

  return data ? transformTemplateFromDB(data) : null;
};

/**
 * Get template by ID
 */
export const getTemplateById = async (id: string): Promise<LoadLetterTemplate | null> => {
  const { data, error } = await supabase
    .from('load_letter_templates')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching template:', error);
    throw error;
  }

  return data ? transformTemplateFromDB(data) : null;
};

/**
 * Create a new template
 */
export const createTemplate = async (
  template: Omit<LoadLetterTemplate, 'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'version'>
): Promise<LoadLetterTemplate> => {
  const { data, error } = await supabase
    .from('load_letter_templates')
    .insert([{
      name: template.name,
      description: template.description,
      header_html: template.headerHtml,
      introduction_html: template.introductionHtml,
      model_explanation_html: template.modelExplanationHtml,
      yard_duty_html: template.yardDutyHtml,
      footer_html: template.footerHtml,
      is_default: template.isDefault,
      is_active: template.isActive,
      semester: template.semester,
      created_by: template.createdBy,
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating template:', error);
    throw error;
  }

  return data;
};

/**
 * Update template
 */
export const updateTemplate = async (
  id: string,
  updates: Partial<LoadLetterTemplate>
): Promise<LoadLetterTemplate> => {
  const updateData: any = {};

  if (updates.name) updateData.name = updates.name;
  if (updates.description !== undefined) updateData.description = updates.description;
  if (updates.headerHtml) updateData.header_html = updates.headerHtml;
  if (updates.introductionHtml) updateData.introduction_html = updates.introductionHtml;
  if (updates.modelExplanationHtml) updateData.model_explanation_html = updates.modelExplanationHtml;
  if (updates.yardDutyHtml) updateData.yard_duty_html = updates.yardDutyHtml;
  if (updates.footerHtml) updateData.footer_html = updates.footerHtml;

  // New template fields
  if (updates.letterOpeningHtml !== undefined) updateData.letter_opening_html = updates.letterOpeningHtml;
  if (updates.letterBodyHtml !== undefined) updateData.letter_body_html = updates.letterBodyHtml;
  if (updates.letterClosingHtml !== undefined) updateData.letter_closing_html = updates.letterClosingHtml;
  if (updates.meetingScheduleHtml !== undefined) updateData.meeting_schedule_html = updates.meetingScheduleHtml;
  if (updates.notesSectionHtml !== undefined) updateData.notes_section_html = updates.notesSectionHtml;
  if (updates.signatureHtml !== undefined) updateData.signature_html = updates.signatureHtml;
  if (updates.logoUrl !== undefined) updateData.logo_url = updates.logoUrl;
  if (updates.showLogo !== undefined) updateData.show_logo = updates.showLogo;
  if (updates.showOratoryLeader !== undefined) updateData.show_oratory_leader = updates.showOratoryLeader;
  if (updates.showPolPosition !== undefined) updateData.show_pol_position = updates.showPolPosition;

  if (updates.isDefault !== undefined) updateData.is_default = updates.isDefault;
  if (updates.isActive !== undefined) updateData.is_active = updates.isActive;
  if (updates.semester !== undefined) updateData.semester = updates.semester;
  if (updates.lastModifiedBy) updateData.last_modified_by = updates.lastModifiedBy;

  const { data, error } = await supabase
    .from('load_letter_templates')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating template:', error);
    throw error;
  }

  return transformTemplateFromDB(data);
};

// Alias for better clarity
export const updateLoadLetterTemplate = updateTemplate;

/**
 * Delete template
 */
export const deleteTemplate = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('load_letter_templates')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

// ============================================================================
// SETTINGS OPERATIONS
// ============================================================================

/**
 * Get system settings
 */
/**
 * Transform database row (snake_case) to LoadLetterSettings (camelCase)
 */
const transformSettingsFromDB = (row: any): LoadLetterSettings => {
  return {
    id: row.id,
    currentAcademicYear: row.current_academic_year,
    semester1StartDate: row.semester1_start_date,
    semester1EndDate: row.semester1_end_date,
    semester2StartDate: row.semester2_start_date,
    semester2EndDate: row.semester2_end_date,
    defaultBaseMinutes: row.default_base_minutes,
    defaultFTE: row.default_fte,
    minutesPerLine: row.minutes_per_line,
    underUtilizedThreshold: row.under_utilized_threshold,
    balancedLowerThreshold: row.balanced_lower_threshold,
    balancedUpperThreshold: row.balanced_upper_threshold,
    overUtilizedThreshold: row.over_utilized_threshold,
    defaultAcknowledgmentDays: row.default_acknowledgment_days,
    enableEmailNotifications: row.enable_email_notifications,
    enableReminderEmails: row.enable_reminder_emails,
    reminderDaysBeforeDue: row.reminder_days_before_due,
    defaultTemplateId: row.default_template_id,
    principalName: row.principal_name,
    collegeName: row.college_name,
    timetablingTeamEmails: row.timetabling_team_emails || [],
    collegeLogoUrl: row.college_logo_url,
    principalSignatureUrl: row.principal_signature_url,
    collegeAddress: row.college_address,
    collegePhone: row.college_phone,
    collegeEmail: row.college_email,
    collegeWebsite: row.college_website,
    collegeTagline: row.college_tagline,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    updatedBy: row.updated_by,
  };
};

export const getLoadLetterSettings = async (): Promise<LoadLetterSettings | null> => {
  const { data, error } = await supabase
    .from('load_letter_settings')
    .select('*')
    .single();

  if (error) {
    console.error('Error fetching settings:', error);
    throw error;
  }

  return data ? transformSettingsFromDB(data) : null;
};

/**
 * Update system settings
 */
export const updateLoadLetterSettings = async (
  updates: Partial<LoadLetterSettings>
): Promise<LoadLetterSettings> => {
  const updateData: any = {};

  if (updates.currentAcademicYear) updateData.current_academic_year = updates.currentAcademicYear;
  if (updates.semester1StartDate) updateData.semester1_start_date = updates.semester1StartDate;
  if (updates.semester1EndDate) updateData.semester1_end_date = updates.semester1EndDate;
  if (updates.semester2StartDate) updateData.semester2_start_date = updates.semester2StartDate;
  if (updates.semester2EndDate) updateData.semester2_end_date = updates.semester2EndDate;
  if (updates.defaultBaseMinutes) updateData.default_base_minutes = updates.defaultBaseMinutes;
  if (updates.defaultFTE) updateData.default_fte = updates.defaultFTE;
  if (updates.minutesPerLine) updateData.minutes_per_line = updates.minutesPerLine;
  if (updates.underUtilizedThreshold) updateData.under_utilized_threshold = updates.underUtilizedThreshold;
  if (updates.balancedLowerThreshold) updateData.balanced_lower_threshold = updates.balancedLowerThreshold;
  if (updates.balancedUpperThreshold) updateData.balanced_upper_threshold = updates.balancedUpperThreshold;
  if (updates.overUtilizedThreshold) updateData.over_utilized_threshold = updates.overUtilizedThreshold;
  if (updates.defaultAcknowledgmentDays) updateData.default_acknowledgment_days = updates.defaultAcknowledgmentDays;
  if (updates.enableEmailNotifications !== undefined) updateData.enable_email_notifications = updates.enableEmailNotifications;
  if (updates.enableReminderEmails !== undefined) updateData.enable_reminder_emails = updates.enableReminderEmails;
  if (updates.reminderDaysBeforeDue) updateData.reminder_days_before_due = updates.reminderDaysBeforeDue;
  if (updates.defaultTemplateId) updateData.default_template_id = updates.defaultTemplateId;
  if (updates.principalName) updateData.principal_name = updates.principalName;
  if (updates.collegeName) updateData.college_name = updates.collegeName;
  if (updates.timetablingTeamEmails) updateData.timetabling_team_emails = updates.timetablingTeamEmails;
  if (updates.updatedBy) updateData.updated_by = updates.updatedBy;

  const { data, error } = await supabase
    .from('load_letter_settings')
    .update(updateData)
    .eq('id', '00000000-0000-0000-0000-000000000001')
    .select()
    .single();

  if (error) {
    console.error('Error updating settings:', error);
    throw error;
  }

  return transformSettingsFromDB(data);
};

// ============================================================================
// QUERY OPERATIONS
// ============================================================================

/**
 * Get queries for a load letter
 */
export const getQueriesByLetterId = async (letterId: string): Promise<LoadLetterQuery[]> => {
  const { data, error } = await supabase
    .from('load_letter_queries')
    .select('*')
    .eq('load_letter_id', letterId)
    .order('submitted_date', { ascending: false });

  if (error) {
    console.error('Error fetching queries:', error);
    throw error;
  }

  return data || [];
};

/**
 * Create a query
 */
export const createQuery = async (
  query: Omit<LoadLetterQuery, 'id' | 'createdAt' | 'updatedAt'>
): Promise<LoadLetterQuery> => {
  const { data, error } = await supabase
    .from('load_letter_queries')
    .insert([{
      load_letter_id: query.loadLetterId,
      staff_id: query.staffId,
      staff_name: query.staffName,
      subject: query.subject,
      description: query.description,
      submitted_date: query.submittedDate,
      status: query.status,
      response: query.response,
      response_date: query.responseDate,
      responded_by: query.respondedBy,
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating query:', error);
    throw error;
  }

  return data;
};

/**
 * Update query (respond to query)
 */
export const updateQuery = async (
  id: string,
  updates: Partial<LoadLetterQuery>
): Promise<LoadLetterQuery> => {
  const updateData: any = {};

  if (updates.status) updateData.status = updates.status;
  if (updates.response !== undefined) updateData.response = updates.response;
  if (updates.responseDate) updateData.response_date = updates.responseDate;
  if (updates.respondedBy) updateData.responded_by = updates.respondedBy;

  const { data, error } = await supabase
    .from('load_letter_queries')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating query:', error);
    throw error;
  }

  return data;
};

// ============================================================================
// ANALYTICS & REPORTING
// ============================================================================

/**
 * Get acknowledgment summary
 */
export const getAcknowledgmentSummary = async (
  semester: Semester,
  academicYear: string
): Promise<AcknowledgmentSummary> => {
  const { data, error } = await supabase
    .from('load_letters')
    .select('status')
    .eq('semester', semester)
    .eq('academic_year', academicYear);

  if (error) {
    console.error('Error fetching acknowledgment summary:', error);
    throw error;
  }

  const totalLetters = data.length;
  const acknowledged = data.filter(l => l.status === 'acknowledged').length;
  const pending = data.filter(l => l.status === 'released').length;
  const queried = data.filter(l => l.status === 'queried').length;

  // TODO: Calculate overdue based on due_date
  const overdue = 0;

  return {
    semester,
    academicYear,
    totalLetters,
    acknowledged,
    pending,
    overdue,
    queried,
    percentAcknowledged: totalLetters > 0 ? Math.round((acknowledged / totalLetters) * 100) : 0,
  };
};

/**
 * Get utilization distribution
 */
export const getUtilizationDistribution = async (
  semester: Semester,
  academicYear: string
): Promise<UtilizationDistribution> => {
  const { data, error } = await supabase
    .from('load_letters')
    .select('utilization, department')
    .eq('semester', semester)
    .eq('academic_year', academicYear);

  if (error) {
    console.error('Error fetching utilization distribution:', error);
    throw error;
  }

  const underUtilized = data.filter(l => l.utilization < 95).length;
  const balanced = data.filter(l => l.utilization >= 95 && l.utilization <= 105).length;
  const overUtilized = data.filter(l => l.utilization > 105).length;

  const totalUtilization = data.reduce((sum, l) => sum + l.utilization, 0);
  const averageUtilization = data.length > 0 ? Math.round(totalUtilization / data.length) : 0;

  // Group by department
  const departmentMap = new Map<string, { total: number; count: number }>();
  data.forEach(letter => {
    const dept = letter.department;
    if (!departmentMap.has(dept)) {
      departmentMap.set(dept, { total: 0, count: 0 });
    }
    const deptData = departmentMap.get(dept)!;
    deptData.total += letter.utilization;
    deptData.count += 1;
  });

  const byDepartment = Array.from(departmentMap.entries()).map(([department, data]) => ({
    department,
    averageUtilization: Math.round(data.total / data.count),
    staffCount: data.count,
  }));

  return {
    semester,
    academicYear,
    underUtilized,
    balanced,
    overUtilized,
    averageUtilization,
    byDepartment,
  };
};
